import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FolderTree, FileText, ChevronRight, ChevronDown, 
  Search, Loader2, Play, Eye, ArrowLeft
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

export default function FormsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId'); // If coming from a case
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [forms, setForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [runningForm, setRunningForm] = useState(null);

  useEffect(() => {
    fetchUser();
    fetchCategories();
    fetchForms();
  }, []);

  useEffect(() => {
    filterForms();
  }, [selectedCategory, searchQuery, forms]);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        if (userData.app_mode !== 'crm') {
          toast.error('Forms require CRM subscription');
          navigate('/dashboard');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/forms-library/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchForms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/forms-library/forms?active_only=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForms(data);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

  const filterForms = () => {
    let filtered = forms;
    
    if (selectedCategory) {
      filtered = filtered.filter(f => f.category_id === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(f => 
        f.form_name.toLowerCase().includes(query) ||
        f.form_code.toLowerCase().includes(query) ||
        f.description.toLowerCase().includes(query)
      );
    }
    
    setFilteredForms(filtered);
  };

  const handleRunForm = async (formId) => {
    if (!caseId) {
      toast.error('Please select a case first');
      navigate('/cases');
      return;
    }

    setRunningForm(formId);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API}/forms/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          form_id: formId,
          case_id: caseId
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success('Form generated successfully!');
        navigate(`/cases/${caseId}?tab=documents`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate form');
      }
    } catch (error) {
      toast.error('Failed to generate form');
    } finally {
      setRunningForm(null);
    }
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const renderCategoryTree = (parentId = null, level = 0) => {
    const children = categories.filter(c => c.parent_id === parentId);
    
    return children.map(category => {
      const hasChildren = categories.some(c => c.parent_id === category.category_id);
      const isExpanded = expandedCategories.has(category.category_id);
      const formsCount = forms.filter(f => f.category_id === category.category_id).length;
      const isSelected = selectedCategory === category.category_id;
      
      return (
        <div key={category.category_id}>
          <div 
            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
              isSelected ? 'bg-violet-100 text-violet-700' : 'hover:bg-slate-100'
            }`}
            style={{ paddingLeft: level * 20 + 8 }}
            onClick={() => setSelectedCategory(isSelected ? null : category.category_id)}
          >
            {hasChildren && (
              <button 
                onClick={(e) => { e.stopPropagation(); toggleCategory(category.category_id); }} 
                className="p-1"
              >
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
            {!hasChildren && <div className="w-6" />}
            
            <FolderTree className="w-4 h-4" />
            <span className="flex-1 text-sm font-medium">{category.name}</span>
            <span className="text-xs text-slate-500">{formsCount}</span>
          </div>
          
          {isExpanded && renderCategoryTree(category.category_id, level + 1)}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8 lg:pt-8 pt-20">
            
            {/* Header */}
            <div className="mb-6">
              {caseId && (
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/cases/${caseId}`)}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Case
                </Button>
              )}
              <h1 className="text-3xl font-bold text-slate-900">Forms Library</h1>
              <p className="text-slate-500 mt-1">Select a form to generate for your case</p>
            </div>

            <div className="grid grid-cols-12 gap-6">
              {/* Categories Sidebar */}
              <div className="col-span-3">
                <div className="bg-white rounded-2xl border border-slate-200 p-4 sticky top-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Categories</h3>
                  <div className="space-y-1">
                    <div 
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        !selectedCategory ? 'bg-violet-100 text-violet-700' : 'hover:bg-slate-100'
                      }`}
                      onClick={() => setSelectedCategory(null)}
                    >
                      <FileText className="w-4 h-4" />
                      <span className="flex-1 text-sm font-medium">All Forms</span>
                      <span className="text-xs text-slate-500">{forms.length}</span>
                    </div>
                    {renderCategoryTree()}
                  </div>
                </div>
              </div>

              {/* Forms List */}
              <div className="col-span-9">
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search forms by name or code..."
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Forms Grid */}
                {filteredForms.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No forms found</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {filteredForms.map(form => (
                      <div 
                        key={form.form_id}
                        className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-violet-200 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-6 h-6 text-blue-600" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{form.form_name}</h3>
                              {form.form_code && (
                                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-medium">
                                  {form.form_code}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mb-2">{form.category_name}</p>
                            {form.description && (
                              <p className="text-sm text-slate-600">{form.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                              <span>{form.total_fields} auto-filled fields</span>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleRunForm(form.form_id)}
                              disabled={!caseId || runningForm === form.form_id}
                              className="rounded-lg bg-violet-600 hover:bg-violet-700"
                            >
                              {runningForm === form.form_id ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                              ) : (
                                <><Play className="w-4 h-4 mr-2" /> Generate Form</>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
